import { useMemo, useState } from 'react';
import { PageHeader } from '@/components/common/PageHeader';
import { FilterBar } from '@/components/common/FilterBar';
import { Spinner } from '@/components/common/Spinner';
import { ErrorState } from '@/components/common/ErrorState';
import { RowActionButtons } from '@/components/tables/RowActionButtons';
import { DataGrid } from '@/components/tables/DataGrid';
import type { DataGridColumn } from '@/components/tables/DataGrid';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { Select2 } from '@/components/forms/Select2';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { useToast } from '@/components/common/toast';
import { usePermission } from '@/permissions/usePermission';
import { emailRecipientsApi } from '@/api/services/emailRecipients';
import type { EmailRecipientDto, EmailRecipientGroup } from '@/api/types/emailRecipients';
import { ApiError } from '@/api/client/abpError';
import { RecipientFormModal } from './RecipientFormModal';
import type { RecipientFormValues } from './RecipientFormModal';
import { GroupSettingsCard } from './GroupSettingsCard';
import { EmailRecipientPermissions } from './permissions';
import {
  useCreateRecipient,
  useDeleteRecipient,
  useGroupSettings,
  useUpdateRecipient,
} from './api';

type ModalState =
  | { mode: 'create' }
  | { mode: 'edit'; id: string; email: string; groupLabel: string }
  | null;

export function EmailRecipientsPage() {
  const can = usePermission();
  const canCreate = can(EmailRecipientPermissions.create);
  const canUpdate = can(EmailRecipientPermissions.update);
  const canDelete = can(EmailRecipientPermissions.delete);

  const toast = useToast();
  const settingsQuery = useGroupSettings();
  const createMut = useCreateRecipient();
  const updateMut = useUpdateRecipient();
  const deleteMut = useDeleteRecipient();

  const [groupFilter, setGroupFilter] = useState<string>('');
  const [modal, setModal] = useState<ModalState>(null);
  const [deleteTarget, setDeleteTarget] = useState<EmailRecipientDto | null>(null);

  const extraParams = useMemo(
    () => (groupFilter ? { group: Number(groupFilter) as EmailRecipientGroup } : {}),
    [groupFilter],
  );

  const table = useServerDataTable<EmailRecipientDto, { group?: EmailRecipientGroup }>({
    queryKey: ['emailRecipients', 'list'],
    fetcher: (req) => emailRecipientsApi.getListForDataTable(req),
    extraParams,
    pageSize: 5,
  });

  const groupOptions = useMemo(
    () => (settingsQuery.data ?? []).map((s) => ({ value: s.group, label: s.groupName })),
    [settingsQuery.data],
  );

  const columns: DataGridColumn<EmailRecipientDto>[] = [
    { key: 'group', header: 'Group', sortable: true, cell: (r) => r.group },
    { key: 'email', header: 'Email', sortable: true, filterable: true, filterKey: 'email', cell: (r) => r.email },
  ];

  function reportError(err: unknown, fallback: string) {
    toast.error(err instanceof ApiError ? err.message : fallback);
  }

  function handleSubmit(values: RecipientFormValues) {
    if (!modal) return;
    if (modal.mode === 'create') {
      createMut.mutate(
        { group: Number(values.group) as EmailRecipientGroup, email: values.email.trim() },
        {
          onSuccess: () => {
            toast.success('Recipient added.');
            setModal(null);
          },
          onError: (err) => reportError(err, 'Failed to add the recipient.'),
        },
      );
    } else {
      updateMut.mutate(
        { id: modal.id, input: { email: values.email.trim() } },
        {
          onSuccess: () => {
            toast.success('Recipient updated.');
            setModal(null);
          },
          onError: (err) => reportError(err, 'Failed to update the recipient.'),
        },
      );
    }
  }

  function confirmDelete() {
    if (!deleteTarget) return;
    deleteMut.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast.success('Recipient removed.');
        setDeleteTarget(null);
      },
      onError: (err) => {
        reportError(err, 'Failed to remove the recipient.');
        setDeleteTarget(null);
      },
    });
  }

  if (settingsQuery.isLoading) return <Spinner />;
  if (settingsQuery.isError)
    return <ErrorState message="Could not load email recipient settings." onRetry={() => settingsQuery.refetch()} />;

  const showRowActions = canUpdate || canDelete;

  return (
    <div>
      <PageHeader title="Email Recipients" breadcrumb={['Home', 'Email Recipients']} />

      <GroupSettingsCard settings={settingsQuery.data ?? []} canUpdate={canUpdate} />

      <FilterBar
        fields={[
          {
            node: (
              <Select2
                label="Group"
                placeholder="All Groups"
                value={groupFilter}
                onChange={(v) => setGroupFilter(v ?? '')}
                options={groupOptions}
              />
            ),
            width: 200,
          },
        ]}
      />

      <div className="card">
        <div className="card-body">
          <DataGrid
            controller={table}
            columns={columns}
            rowKey={(r) => r.id}
            emptyMessage="No recipients found."
            toolbarEnd={
              canCreate && (
                <button type="button" className="btn btn-primary btn-sm" onClick={() => setModal({ mode: 'create' })}>
                  <i className="me-1" />
                  Add Recipient
                </button>
              )
            }
            rowActions={
              showRowActions
                ? (row) => (
                    <RowActionButtons
                      onEdit={
                        canUpdate
                          ? () => setModal({ mode: 'edit', id: row.id, email: row.email, groupLabel: row.group })
                          : undefined
                      }
                      onDelete={canDelete ? () => setDeleteTarget(row) : undefined}
                    />
                  )
                : undefined
            }
          />
        </div>
      </div>

      {modal && (
        <RecipientFormModal
          show
          mode={modal.mode}
          groupOptions={groupOptions}
          initial={modal.mode === 'edit' ? { email: modal.email, groupLabel: modal.groupLabel } : undefined}
          busy={createMut.isPending || updateMut.isPending}
          onSubmit={handleSubmit}
          onClose={() => setModal(null)}
        />
      )}

      <ConfirmDialog
        show={deleteTarget !== null}
        title="Remove recipient"
        message={`Remove ${deleteTarget?.email ?? ''}? This cannot be undone.`}
        confirmLabel="Remove"
        busy={deleteMut.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
